import React, { useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import Hls from 'hls.js';

interface VideoPlayerProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  src: string;
  style?: React.CSSProperties;
  onHlsError?: (error: any) => void;
}

/**
 * A video player that transparently handles both HLS (.m3u8) streams
 * and regular video files (mp4, webm, etc.).
 *
 * - If the browser natively supports HLS (e.g. Safari), it falls back to
 *   the native <video> element.
 * - Otherwise it uses hls.js to parse and play HLS streams.
 * - Non-HLS sources are played directly via the native <video> element.
 */
const VideoPlayer = forwardRef<HTMLVideoElement, VideoPlayerProps>(({ src, style, onHlsError, ...rest }, ref) => {
  const internalRef = useRef<HTMLVideoElement>(null);
  const hlsRef = useRef<Hls | null>(null);
  // Always holds the latest onHlsError callback without causing the effect to re-run.
  const onHlsErrorRef = useRef(onHlsError);

  useImperativeHandle(ref, () => internalRef.current!);

  // Keep the ref in sync on every render so the effect always calls the latest version.
  useEffect(() => {
    onHlsErrorRef.current = onHlsError;
  });

  const isHlsSrc = (url: string) => url?.includes('.m3u8') || url?.includes('application/x-mpegURL');

  // Synchronously compute if hls.js should handle playback.
  // This avoids a state-driven re-render that initially sets `src` and then removes it,
  // which clobbers the imperatively set blob URL of hls.js on Chrome.
  const useHlsJs = isHlsSrc(src) && Hls.isSupported();

  useEffect(() => {
    const video = internalRef.current;

    if (!video || !src) {
      return undefined;
    }

    // Destroy any existing hls instance before creating a new one
    // istanbul ignore next
    if (hlsRef.current) {
      hlsRef.current.destroy();
      hlsRef.current = null;
    }

    if (useHlsJs) {
      const hls = new Hls({ enableWorker: true, lowLatencyMode: false });
      hlsRef.current = hls;

      hls.loadSource(src);
      hls.attachMedia(video);

      hls.on(Hls.Events.ERROR, (_event, data) => {
        if (data.fatal) {
          switch (data.type) {
            case Hls.ErrorTypes.NETWORK_ERROR:
              hls.startLoad();
              break;
            case Hls.ErrorTypes.MEDIA_ERROR:
              hls.recoverMediaError();
              break;
            default:
              hls.destroy();
              onHlsErrorRef.current?.(data);
              break;
          }
        }
      });
    } else if (isHlsSrc(src) && !video.canPlayType('application/vnd.apple.mpegurl')) {
      console.warn('HLS is not supported in this browser.');
      onHlsErrorRef.current?.({ message: 'HLS not supported' });
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [src, useHlsJs]);

  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <video
      ref={internalRef}
      style={style}
      {...rest}
      // Only withhold src from React when hls.js owns the media element.
      // For Safari native HLS and plain video, pass src normally so React
      // stays in sync and never clobbers an imperatively-set value.
      src={useHlsJs ? undefined : src}
    />
  );
});

VideoPlayer.displayName = 'VideoPlayer';
export default VideoPlayer;
